import { type FuelOptimizerLegPlan } from '@eflight/shared/dist/fuelChain';
import { useMemo } from 'react';

import { type UpcomingReservationEvent } from '../../Datasource/FlightSupplementalData/Datasources/useUpcomingReservations';

/**
 * (#3029) Palivové VSTUPY pilotních obrazovek z uložené osy.
 *
 * Pilotní řetěz (`byLegFuelData`) zůstává beze změny — jen dostane jiná dvě čísla, ze kterých
 * odvozuje všechno ostatní: seed rezervace (`fuelStart`) a uplift každého legu. Tím se na pilota
 * přenese to podstatné, co se přepnutím mění: **volba dispečera**. Uplift v ose už není zamrzlá
 * hodnota z rezervace, ale to, co OPS větev skutečně rozhodla natankovat.
 *
 * Proč podstrčení vstupů a ne čtení hotových čísel z osy: pilotní appka je offline-first. Kdyby
 * obrazovka jen mapovala uložené hodnoty, pilot by bez signálu neviděl NIC — a spoléhat se
 * offline na `reservation.fuelStart` / `childEvent.fuelUplift` taky nejde, ty po přepnutí nikdo
 * neaktualizuje (u rezervace založené po přepnutí by uplift byl 0). Proto se tyhle dvě hodnoty
 * ukládají do lokální databáze (`Datasources/FuelPlans`) a čtou se odtamtud online i offline.
 *
 * ⚠️ Cena tohohle rozhodnutí: EBRN, MREQ a strop se pilotovi dál počítají lokálně, takže se ve
 * výjimečných případech můžou lišit od dispečera (typicky u odlétaného legu, kde engine bere
 * hodnoty z OFP). Vědomý kompromis mezi „stejná čísla všude" a „pilot o čísla nepřijde".
 */

/** Tvar, který se ukládá do lokální databáze. */
export type AxisFuelInputs = {
    /** `ChildEvent.id` → uplift v jednotce paliva profilu. */
    upliftByEventId: Record<string, number>;
    /** `Reservation.id` → FOB PRVNÍHO legu rezervace; fallback seed řetězu. */
    seedByReservationId: Record<string, number>;
    /**
     * (#3087) `ChildEvent.id` → FOB legu v jednotce profilu — hodnota, kterou OPS větev enginu
     * ZŘETĚZILA, u odlétaných legů ze skutečně naměřeného stavu nádrže. Volitelné kvůli záznamům
     * uloženým starší verzí; bez něj řetěz dopočítává FOB sám jako dřív.
     *
     * Proč nestačí seed + uplift: řetěz si FOB dalších legů odvozoval `block − plánovaný EBRN`,
     * jenže engine po doletu seeduje z NAMĚŘENÉHO paliva — a skutečná spotřeba se od plánované
     * běžně liší. Ostrý případ OK-AML 28.08.2026: predikce 606 kg vs. naměřených 715 kg, drift
     * 109 kg shodil zobrazený Plan pod MREQ (1451 < 1560). Žádný lokální vzorec naměřenou hodnotu
     * nereprodukuje, takže ji pilot musí dostat z osy.
     */
    fobByEventId?: Record<string, number>;
};

const finite = (value: number | undefined): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * Projekce plánů osy do vstupů. Jediné místo, kde se rozhoduje, CO z osy pilot potřebuje —
 * volá ji synchronizace do lokální databáze, aby se online a offline uložilo totéž.
 */
export function axisFuelInputsFromPlans(
    events: UpcomingReservationEvent[],
    plans: ReadonlyMap<string, FuelOptimizerLegPlan>
): AxisFuelInputs {
    const upliftByEventId: Record<string, number> = {};
    const seedByReservationId: Record<string, number> = {};
    const fobByEventId: Record<string, number> = {};

    for (const event of events) {
        const { reservation } = event;
        for (const leg of reservation.events ?? []) {
            const uplift = finite(plans.get(leg.id)?.plan.fuelUpliftVolume?.volume);
            if (uplift != null) {
                upliftByEventId[leg.id] = uplift;
            }
            const fob = finite(plans.get(leg.id)?.plan.startFuelVolume?.volume);
            if (fob != null) {
                fobByEventId[leg.id] = fob;
            }
        }
        const firstLeg = (reservation.events ?? [])[0];
        // Legy bez plánu seed neposkytují — sáhnout na pozdější by posunulo celou rezervaci.
        const seed = firstLeg == null ? undefined : finite(plans.get(firstLeg.id)?.plan.startFuelVolume?.volume);
        if (seed != null) {
            seedByReservationId[reservation.id] = seed;
        }
    }

    return { upliftByEventId, seedByReservationId, fobByEventId };
}

/**
 * Vrátí tytéž události s palivovými poli přepsanými z osy. Když osa nemá co nabídnout, vrací
 * PŮVODNÍ pole beze změny — na identitě stojí memo konzumentů i to, že se bez dat nic nepřepočítá.
 */
export function withAxisFuelInputs(
    events: UpcomingReservationEvent[],
    inputs: AxisFuelInputs | undefined
): UpcomingReservationEvent[] {
    if (inputs == null) {
        return events;
    }
    // Rezervace se mezi událostmi sdílí (víc legů téže rezervace), takže se přepisuje jednou
    // a výsledek sdílí — jinak by řetěz dostal několik různých instancí téže rezervace.
    const patchedByReservationId = new Map<string, UpcomingReservationEvent['reservation']>();
    let changed = false;

    const patchReservation = (reservation: UpcomingReservationEvent['reservation']) => {
        const cached = patchedByReservationId.get(reservation.id);
        if (cached != null) {
            return cached;
        }
        const legs = reservation.events ?? [];
        let legChanged = false;
        const patchedLegs = legs.map((leg) => {
            const uplift = inputs.upliftByEventId[leg.id];
            if (uplift == null || uplift === leg.fuelUplift) {
                return leg;
            }
            legChanged = true;

            return { ...leg, fuelUplift: uplift };
        });
        const seed = inputs.seedByReservationId[reservation.id];
        const seedChanged = seed != null && seed !== reservation.fuelStart;
        if (!legChanged && !seedChanged) {
            patchedByReservationId.set(reservation.id, reservation);

            return reservation;
        }
        changed = true;
        const patched = {
            ...reservation,
            ...(seedChanged ? { fuelStart: seed } : {}),
            ...(legChanged ? { events: patchedLegs } : {}),
        };
        patchedByReservationId.set(reservation.id, patched);

        return patched;
    };

    const patchedEvents = events.map((event) => {
        const reservation = patchReservation(event.reservation);
        if (reservation === event.reservation) {
            return event;
        }
        const leg = reservation.events?.find((candidate) => candidate.id === event.event.id);

        return { ...event, reservation, event: leg ?? event.event };
    });

    return changed ? patchedEvents : events;
}

/** Hooková podoba — memo drží identitu, dokud se vstupy nebo události nezmění. */
export function useAxisFuelInputs(
    events: UpcomingReservationEvent[],
    inputs: AxisFuelInputs | undefined
): UpcomingReservationEvent[] {
    return useMemo(() => withAxisFuelInputs(events, inputs), [events, inputs]);
}
